import { Button, TDeviceType, UIHeading4, UIHeading5, useDeviceType } from '@cian/ui-kit';
import { clsx } from 'clsx';
import React, { FC, useCallback } from 'react';

import { DobroFormPart } from '../DobroFormPart';
import { DobroFormText } from '../DobroFormText';
import * as styles from './DobroForm.css';
import logoImgUrl from './logo.svg';

interface IDobroFormProps {
  loading: boolean;
  webview: boolean;
  onSubmit?: () => void;

  objectType: React.ReactNode;
  moscow: React.ReactNode;
  location: React.ReactNode;
  address: React.ReactNode;
  floor: React.ReactNode;

  roomsCount: React.ReactNode;
  area: React.ReactNode;
  guestsCount: React.ReactNode;
  bedsCount: React.ReactNode;
  bedsDescription: React.ReactNode;

  facilities: React.ReactNode;

  availablePeriod: React.ReactNode;
  priceType: React.ReactNode;
  priceDescription: React.ReactNode;
  availableFor: React.ReactNode;
  readyAccommodate: React.ReactNode;
  otherWishes: React.ReactNode;

  name: React.ReactNode;
  phone: React.ReactNode;
  additionalPhone: React.ReactNode;
  telegram: React.ReactNode;
  email: React.ReactNode;

  offerSigned: React.ReactNode;
}

type TLayout = 'webview' | 'desktop' | 'mobile';

function getLayout(webview: boolean, deviceType: TDeviceType): TLayout {
  return webview ? 'webview' : deviceType === 'desktop' ? 'desktop' : 'mobile';
}

export const DobroForm: FC<IDobroFormProps> = props => {
  const { onSubmit, loading } = props;
  const deviceType = useDeviceType();
  const layout = getLayout(props.webview, deviceType);

  const _onSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (loading) {
        return;
      }
      onSubmit?.();
    },
    [onSubmit, loading],
  );

  return (
    <form
      onSubmit={_onSubmit}
      className={clsx({
        [styles.mobile]: layout === 'mobile',
        [styles.desktop]: layout === 'desktop',
        [styles.webvew]: layout === 'webview',
      })}
    >
      {layout !== 'webview' && (
        <div className={styles.header}>
          <UIHeading5>Заявка в&nbsp;фонд</UIHeading5>
          {layout === 'desktop' && (
            <div className={styles.header_info}>
              <img src={logoImgUrl} />
              <div className={styles.subheader}>
                <UIHeading4>
                  Расскажите о жилье, в&nbsp;котором готовы принять жителей Курской области
                  и&nbsp;других&nbsp;пострадавших регионов
                </UIHeading4>
              </div>
              <DobroFormText>Сотрудники фонда перезвонят, чтобы&nbsp;уточнить подробности</DobroFormText>
            </div>
          )}
        </div>
      )}
      <div className={styles.content}>
        {layout !== 'desktop' && (
          <DobroFormPart>
            <DobroFormText>
              Расскажите о&nbsp;жилье, в&nbsp;котором готовы принять жителей&nbsp;Курской области и&nbsp;других
              пострадавших регионов. Сотрудники фонда перезвонят, чтобы&nbsp;уточнить подробности.
            </DobroFormText>
          </DobroFormPart>
        )}

        <DobroFormPart title="Расположение">
          {props.objectType}
          {props.moscow}
          <div className={styles.location}>{props.location}</div>
          <div className={styles.address_and_floor}>
            <div className={styles.address}>{props.address}</div>
            <div className={styles.floor}>{props.floor}</div>
          </div>
        </DobroFormPart>

        <DobroFormPart title="Параметры жилья">
          {props.roomsCount}
          <div className={styles.area}>{props.area}</div>
          <div className={styles.guests_count_and_beds_count}>
            <div className={styles.guests_count}>{props.guestsCount}</div>
            <div className={styles.beds_count}>{props.bedsCount}</div>
          </div>
          <div className={styles.beds_description}>{props.bedsDescription}</div>
        </DobroFormPart>

        <DobroFormPart title="В жилье есть">{props.facilities}</DobroFormPart>

        <DobroFormPart title="Условия">
          {props.availablePeriod}
          {props.priceType}
          <div className={styles.price_description}>{props.priceDescription}</div>
          {props.availableFor}
          {props.readyAccommodate}
          <div className={styles.other_wishes}>{props.otherWishes}</div>
        </DobroFormPart>

        <DobroFormPart title="Контакты">
          <div className={styles.contacts_text}>
            <DobroFormText>
              Защитим ваш&nbsp;номер от&nbsp;спама и&nbsp;мошенников: передадим контакты только фонду
            </DobroFormText>
          </div>

          <div className={styles.name}>{props.name}</div>
          <div className={styles.phone}>{props.phone}</div>
          <div className={styles.additional_phone}>{props.additionalPhone}</div>
          <div className={styles.telegram}>{props.telegram}</div>
          <div className={styles.telegram}>{props.email}</div>
        </DobroFormPart>

        <DobroFormPart>
          <div className={styles.offer_signed}>{props.offerSigned}</div>
        </DobroFormPart>

        <div className={styles.footer}>
          <Button type="submit" fullWidth={layout !== 'desktop'} disabled={loading} loading={loading}>
            Отправить заявку
          </Button>
        </div>
      </div>
    </form>
  );
};
